import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { buildDynamicMeetingFlow } from './buildDynamicMeetingFlow';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import saveThreadToFirestore from '../../utils/saveThreadToFirestore';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useParams } from 'react-router-dom';

const containerStyle = {
  backgroundColor: '#f9fafb',
  minHeight: '100vh',
  padding: '40px 20px',
};

const innerStyle = {
  maxWidth: '960px',
  margin: '0 auto',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  padding: '40px',
  fontFamily: `'Helvetica Neue', sans-serif`,
};

const inputStyle = {
  width: '100%',
  marginBottom: '16px',
  padding: '16px',
  borderRadius: '12px',
  border: '1px solid #ccc',
  backgroundColor: '#fdfdfd',
  fontSize: '16px',
  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
};

const labelStyle = {
  fontWeight: '600',
  marginBottom: '6px',
  display: 'block',
  fontSize: '16px',
};

const buttonStyle = {
  marginTop: '16px',
  backgroundColor: '#FFD700',
  color: '#000',
  fontSize: '16px',
  fontWeight: '600',
  padding: '12px 24px',
  borderRadius: '12px',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
};

const resultBoxStyle = {
  marginTop: '40px',
  backgroundColor: '#ffffff',
  padding: '30px',
  borderRadius: '16px',
  border: '1px solid #ccc',
  fontSize: '15px',
};

const chatContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '40px',
};

const chatBubbleStyle = {
  padding: '12px 16px',
  borderRadius: '16px',
  maxWidth: '96.5%',
  whiteSpace: 'pre-wrap',
  lineHeight: '1.5',
  fontSize: '15px',
};

const userBubble = {
  ...chatBubbleStyle,
  backgroundColor: '#e0f0ff',
  alignSelf: 'flex-end',
};

const flickerStyle = {
  animation: 'flicker 0.8s ease-in-out',
};

const alfredBubble = {
  ...chatBubbleStyle,
  backgroundColor: '#f0f0f0',
  alignSelf: 'flex-start',
};

const PrepForm = ({
  existingFormData = null,
  existingChatHistory = [],
  existingPrepName = '',
  existingThreadId = null,
  prepResultFull: existingPrepResultFull = '',
  isReadOnly = false,
  isThread = false
}) => {
  const [formData, setFormData] = useState(existingFormData || {
    meetingGoal: '',
    customGoal: '',
    industry: '',
    companyName: '',
    companyWebsite: '',
    geography: '',
    contactName: '',
    contactRole: '',
    companySize: '',
    relationshipStatus: '',
  });

  const [loading, setLoading] = useState(false);
  const [dots, setDots] = useState('');
  const [prepResult, setPrepResult] = useState('');
  const [prepResultFull, setPrepResultFull] = useState(existingPrepResultFull || '');
  const [dynamicMeetingFlow, setDynamicMeetingFlow] = useState([]);
  const [askText, setAskText] = useState('');
  const [chatHistory, setChatHistory] = useState(existingChatHistory || []);
  const [prepName, setPrepName] = useState(existingPrepName || '');
  const [loadingReply, setLoadingReply] = useState(false);
  const chatEndRef = useRef(null);
  const [isEditingPrep, setIsEditingPrep] = useState(false);
  const [editedPrepText, setEditedPrepText] = useState('');
  const [justSavedPrep, setJustSavedPrep] = useState('');
  const [justCopied, setJustCopied] = useState(false);
  const resultRef = useRef(null);
  const [threadId, setThreadId] = useState(existingThreadId || uuidv4());
  const [threadName, setThreadName] = useState('');
  const [followUps, setFollowUps] = useState([]);
  const auth = getAuth();
  const user = auth.currentUser;
  const { id: threadParamId } = useParams();
  const generateUUID = () => uuidv4();
  const scrollRef = useRef(null);
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  console.log('🧪 SCROLL CHECK', { isThread, prepResult, hasScrolled: hasScrolled.current });
  useEffect(() => {
    if (!isThread && !hasScrolled.current && prepResult && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      hasScrolled.current = true;
    }
  }, [prepResult, isThread]);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    let interval;
    if (loadingReply) {
      interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loadingReply]);

  useEffect(() => {
    const saveFullThread = async () => {
      if (!prepResultFull || chatHistory.length === 0) return;

      const fullConversation = [
        { sender: 'alfred', text: prepResultFull },
        ...chatHistory
      ];

      const combinedText = fullConversation
        .map(m => `${m.sender === 'user' ? 'You' : 'Alfred'}: ${m.text}`)
        .join('\n\n');

      await saveThreadToFirestore({
        module: 'Meeting Prep',
        name: prepName || `${formData.companyName} Prep`,
        content: combinedText,
      });
    };

    saveFullThread();
  }, [chatHistory]);

  useEffect(() => {
    const fetchThread = async () => {
      if (threadParamId && user?.email) {
        const docRef = doc(db, 'users', user.email, 'threads', threadParamId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const thread = docSnap.data();
          setThreadId(thread.id);
          setThreadName(thread.name || 'Untitled Meeting Prep');
          setFollowUps(thread.followUps || []);
        }
      }
    };
    fetchThread();
  }, [threadParamId, user]);

  useEffect(() => {
    if (existingPrepResultFull && !prepResult) {
      const cleaned = existingPrepResultFull.replace(/🧭 Suggested Meeting Flow[\s\S]*/g, '').trim();
      setPrepResult(cleaned);
    }
  }, [existingPrepResultFull]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...formData,
      finalGoal: formData.customGoal || formData.meetingGoal,
    };

    try {
      console.log('🔍 Sending payload:', payload);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/prep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      setPrepResultFull(data.result); // ✅ store full markdown
      const cleaned = data.result.replace(/🧭 Suggested Meeting Flow[\s\S]*/g, '').trim();
        setPrepResult(cleaned);

        await saveThreadToFirestore(user?.uid, {
          id: threadId,
          name:
            prepName ||
            `Meeting Prep: ${formData.companyName || 'Company'} – ${formData.industry || 'Industry'} – ${formData.contactRole || 'Role'} – ${formData.geography || 'Region'} – Size: ${formData.companySize || 'Unknown'}`,
          type: 'prep',
          data: {
            prepResultFull: data.result,
            chat: [], // No follow-ups yet
          },
          metadata: formData,
        });

      try {
        const dynamicFlow = buildDynamicMeetingFlow({
          meetingGoal: formData.customGoal || formData.meetingGoal,
          industry: formData.industry,
          contactRole: formData.contactRole,
          companySize: formData.companySize || 'Midsize',
          relationshipStatus: formData.relationshipStatus || 'New',
        });

        setDynamicMeetingFlow(dynamicFlow);
      } catch (flowError) {
        console.error('❌ buildDynamicMeetingFlow failed:', flowError);
      }

    } catch (err) {
      console.error('❌ Prep generation failed:', err);
      alert('Something went wrong generating the prep.');
    }

    setLoading(false);
  };

  const handleQuestionChange = (stepIndex, questionIndex, newText) => {
    setDynamicMeetingFlow((prevFlow) => {
      const updatedFlow = [...prevFlow];
      updatedFlow[stepIndex].questions[questionIndex] = newText;
      return updatedFlow;
    });
  };

  const handleAskAlfred = async () => {
    if (!askText.trim()) return;

    const updatedChat = [...chatHistory, { sender: 'user', text: askText }];
    setChatHistory(updatedChat);
    setAskText('');
    setLoadingReply(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/prep/ask-alfred`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageHistory: [
            {
              role: 'system',
              content: `This prep is for a meeting with ${formData.contactRole} at ${formData.companyName} in the ${formData.industry} industry, located in ${formData.geography}. The meeting goal is: ${formData.customGoal || formData.meetingGoal}.`
            },
            ...chatHistory.map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text
            })),
            {
              role: 'user',
              content: askText
            }
          ],
          context: {
            meetingGoal: formData.meetingGoal,
            customGoal: formData.customGoal,
            industry: formData.industry,
            geography: formData.geography,
            companyName: formData.companyName,
            companyWebsite: formData.companyWebsite,
            contactName: formData.contactName,
            contactRole: formData.contactRole,
            companySize: formData.companySize,
            relationshipStatus: formData.relationshipStatus
          }
        })
      });

      const data = await response.json();
      const reply = data.result || 'Something went wrong.';
      const updated = [...updatedChat, { sender: 'alfred', text: reply }];
      setChatHistory(updated);

      await saveThreadToFirestore(user?.uid, {
        id: threadId,
        name:
          prepName ||
          `Meeting Prep: ${formData.companyName || 'Company'} – ${formData.industry || 'Industry'} – ${formData.contactRole || 'Role'} – ${formData.geography || 'Region'} – Size: ${formData.companySize || 'Unknown'}`,
        type: 'prep',
        data: {
          prepResultFull,
          chat: updated,
        },
        metadata: formData,
      });

    } catch (err) {
      console.error('❌ Ask Alfred failed:', err);
      setChatHistory(prev => [...prev, { sender: 'alfred', text: 'Something went wrong.' }]);
    } finally {
      setLoadingReply(false);
    }
  };


  const parseMarkdownToSections = (markdown) => {
    const sections = {};
    let currentSection = '';
    let buffer = [];

    markdown.split('\n').forEach(line => {
      const headerMatch = line.match(/^#+\s(.+)/); // Match #, ##, ### etc.

      if (headerMatch) {
        if (currentSection) {
          sections[currentSection] = buffer.join('\n').trim();
        }
        currentSection = headerMatch[1].trim();
        buffer = [];
      } else {
        buffer.push(line);
      }
    });

    if (currentSection) {
      sections[currentSection] = buffer.join('\n').trim();
    }

    return sections;
  };

  const handleExportToWord = () => {
    if (!prepResultFull) return;

    const parsed = parseMarkdownToSections(prepResultFull);

    const paragraphs = [
      new Paragraph({
        children: [new TextRun({ text: 'Meeting Prep Briefing', bold: true, size: 36 })],
        spacing: { after: 400 },
      }),
    ];

    if (Object.keys(parsed).length === 0) {
      prepResultFull.split('\n').forEach((line) => {
        if (line.trim()) {
          paragraphs.push(
            new Paragraph({
              children: [new TextRun({ text: line.trim(), size: 24 })],
              spacing: { after: 100 },
            })
          );
        }
      });
    } else {
      Object.entries(parsed).forEach(([section, text]) => {
        paragraphs.push(
          new Paragraph({
            children: [new TextRun({ text: section, bold: true, size: 28, underline: {} })],
            spacing: { after: 200 },
          })
        );

        text.split('\n').forEach((line) => {
          if (line.trim()) {
            paragraphs.push(
              new Paragraph({
                children: [new TextRun({ text: line.trim(), size: 24 })],
                spacing: { after: 100 },
              })
            );
          }
        });
      });
    }

    const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });

    Packer.toBlob(doc).then((blob) => {
      const cleanDefault = (formData.companyName || 'client').replace(/[^\w\s-]/g, '');
      const cleanCustom = (prepName || '').replace(/[^\w\s-]/g, '');
      const finalName = cleanCustom || `alfred-meeting-prep-${cleanDefault}`;
      saveAs(blob, `${finalName}.docx`);
    });
  };

  return (
    <div style={containerStyle}>
      <div style={innerStyle}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          marginBottom: '30px',
          borderBottom: '4px solid #FFD700',
          display: 'inline-block',
          paddingBottom: '10px'
        }}>
          Prep for a Meeting
        </h2>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Meeting Goal</label>
          <select
            style={inputStyle}
            name="meetingGoal"
            value={formData.meetingGoal}
            onChange={handleChange}
            disabled={isReadOnly}
            className={isReadOnly ? 'disabledInput' : ''}>
            <option value="">-- Select Goal --</option>
            <option value="Uncover needs and establish next steps">Uncover needs and establish next steps</option>
            <option value="Effectively pitch and move to next steps">Effectively pitch and move to next steps</option>
            <option value="Build trust and deepen relationship">Build trust and deepen relationship</option>
            <option value="Renew or expand business">Renew or expand business</option>
          </select>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Or type your own meeting goal (optional)</label>
            <input
              style={inputStyle}
              name="customGoal"
              placeholder="e.g., Get a referral from this longtime client."
              value={formData.customGoal}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Company Industry</label>
            <input
              style={inputStyle}
              name="industry"
              placeholder="e.g., Roofing"
              value={formData.industry}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Geography</label>
            <input
              style={inputStyle}
              name="geography"
              placeholder="e.g., Central Florida"
              value={formData.geography}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Company Name</label>
            <input
              style={inputStyle}
              name="companyName"
              placeholder="e.g., Alfred's Roofing"
              value={formData.companyName}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Company Website (optional)</label>
            <input
              style={inputStyle}
              name="companyWebsite"
              placeholder="e.g., www.AlfredsRoofing.com"
              value={formData.companyWebsite}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Contact Name(s) or LinkedIn URLs (optional)</label>
            <input
              style={inputStyle}
              name="contactName"
              placeholder="e.g., Alfred"
              value={formData.contactName}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <div style={{ width: '96.5%' }}>
            <label style={labelStyle}>Contact Role</label>
            <input
              style={inputStyle}
              name="contactRole"
              placeholder="e.g., CEO"
              value={formData.contactRole}
              onChange={handleChange}
              disabled={isReadOnly}
              className={isReadOnly ? 'disabledInput' : ''}
            />
          </div>

          <label style={labelStyle}>Company Size</label>
          <select
            style={inputStyle}
            name="companySize"
            value={formData.companySize}
            onChange={handleChange}
            disabled={isReadOnly}
            className={isReadOnly ? 'disabledInput' : ''}>
            <option value="">-- Select Size --</option>
            <option value="Micro">Micro (1–10 employees)</option>
            <option value="Small">Small (11–50 employees)</option>
            <option value="Midsize">Midsize (51–250 employees)</option>
            <option value="Large">Large (250+ employees)</option>
          </select>

          <label style={labelStyle}>Relationship Status</label>
          <select
            style={inputStyle}
            name="relationshipStatus"
            value={formData.relationshipStatus}
            onChange={handleChange}
            disabled={isReadOnly}
            className={isReadOnly ? 'disabledInput' : ''}>
            <option value="">-- Select Status --</option>
            <option value="New">New</option>
            <option value="Existing">Existing</option>
          </select>

          <button type="submit" style={buttonStyle} disabled={loading}>
            {loading ? `Alfred is Generating Your Prep${dots} ✏️` : 'Generate Meeting Prep'}
          </button>
        </form>

        {prepResult && (
          <>
            <div ref={scrollRef} style={{ position: 'relative', top: '-80px' }} />
            <div style={resultBoxStyle}>
            <h2 style={{ marginBottom: '20px' }}>📋 Your Meeting Briefing</h2>
            <div style={{ lineHeight: '1.7', fontSize: '16px', paddingBottom: '24px' }}>
              {isEditingPrep ? (
                <>
                  <textarea
                    value={editedPrepText}
                    onChange={(e) => setEditedPrepText(e.target.value)}
                    rows={16}
                    style={{
                      width: '95%',
                      padding: '16px',
                      fontSize: '16px',
                      fontFamily: `'Helvetica Neue', sans-serif`,
                      lineHeight: '1.6',
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      border: '1px solid #ccc',
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
                      whiteSpace: 'pre-wrap'
                    }}
                  />
                  <button
                    onClick={async () => {
                      setPrepResult(editedPrepText);
                      setIsEditingPrep(false);
                      setJustSavedPrep('yes');
                      setTimeout(() => setJustSavedPrep(''), 800);

                      try {
                        await saveThreadToFirestore(user?.uid, {
                          id: threadId,
                          name:
                            prepName ||
                            `Meeting Prep: ${formData.companyName || 'Company'} – ${formData.industry || 'Industry'} – ${formData.contactRole || 'Role'} – ${formData.geography || 'Region'} – Size: ${formData.companySize || 'Unknown'}`,
                          type: 'prep',
                          data: {
                            prepResultFull: editedPrepText,
                            chat: chatHistory,
                          },
                          metadata: formData,
                        });
                        console.log('💾 Thread saved after manual edit');
                      } catch (err) {
                        console.error('❌ Failed to save edited prep:', err);
                      }
                    }}
                    style={{ marginTop: '8px', ...buttonStyle }}
                  >
                    💾 Save
                  </button>
                </>
              ) : (
                <div style={justSavedPrep ? flickerStyle : {}}>
                  <ReactMarkdown>{prepResult}</ReactMarkdown>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      onClick={() => {
                        setIsEditingPrep(true);
                        setEditedPrepText(prepResult);
                      }}
                      style={{
                        backgroundColor: '#f6f6f6',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 16px',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <span role="img" aria-label="edit">📝</span> Edit
                    </button>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(prepResult || ''); // or `prepResult`, etc.
                        setJustCopied(true);
                        setTimeout(() => setJustCopied(false), 1000);
                      }}
                      style={{
                        backgroundColor: '#f6f6f6',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 16px',
                        fontWeight: '600',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      {justCopied ? '✅ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                </div>
              )}
              
              <div style={{ marginTop: '20px' }}>
                <label style={labelStyle}>🏷️ Optional Meeting Prep Name</label>
                <input
                  type="text"
                  placeholder="e.g., March XYZ Company Discovery Prep"
                  value={prepName}
                  onChange={(e) => setPrepName(e.target.value)}
                  style={{
                    width: '96.5%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '10px',
                    border: '1px solid #ccc',
                    marginBottom: '0px'
                  }}
                />
              </div>

              <button
                onClick={handleExportToWord}
                style={{ ...buttonStyle, marginTop: '16px', marginBottom: '16px' }}
              >
                Export Prep to Word
              </button>
            </div>

            {/* Ask Alfred Section */}
            <div style={chatContainerStyle}>
              {chatHistory.map((msg, idx) => (
                <div key={idx} style={msg.sender === 'user' ? userBubble : alfredBubble}>
                  <strong>{msg.sender === 'user' ? 'You' : 'Alfred'}:</strong> <br />
                  {msg.text}
                </div>
              ))}

              {/* Typing Bubble (above textarea) */}
              {loadingReply && (
                <div style={{
                  ...alfredBubble,
                  fontStyle: 'italic',
                  marginTop: '4px',
                  marginBottom: '12px',
                  color: '#333',
                  wordWrap: 'break-word'
                }}>
                  <strong>Alfred:</strong><br />
                  Alfred is typing{dots}
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            <div style={{ marginTop: '24px', width: '96.5%' }}>
              <label style={labelStyle}>Ask Alfred a Follow-up Question:</label>
              <textarea
                style={{
                  ...inputStyle,
                  fontFamily: "'Helvetica Neue', sans-serif"
                }}
                value={askText}
                placeholder="e.g., What are some meaningful questions I can ask to uncover real need?"
                onChange={(e) => setAskText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAskAlfred();
                  }
                }}
              />

              {!loadingReply && (
                <button style={buttonStyle} onClick={handleAskAlfred}>
                  Ask Alfred
                </button>
              )}
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PrepForm;
